/**
 * Les 27 études de cas tirables au sort lors de l'oral CCS 2 Roues.
 *
 * Chaque cas inclut :
 *  - num : numéro tirable (1-27)
 *  - title : libellé court
 *  - familySlug : famille pédagogique (référence au classeur 7 familles)
 *  - enonce : énoncé du sujet tel que présenté au candidat
 *  - publicAttendu : type d'élèves visés (réponse-modèle)
 *  - leviersAttendus[] : 3-5 leviers attendus
 *  - freinsAttendus[] : 3-5 freins attendus
 *  - objectifs : { connaitre, comprendre, agir } — objectifs intermédiaires attendus
 *  - methodesAttendues[] : méthodes pédagogiques attendues
 *  - supportsAttendus[] : supports recommandés
 *  - evaluationAttendue : description de l'évaluation finale attendue
 *  - pointsCles[] : phrases-clés à mobiliser face au jury
 *
 * Source : structure pédagogique CCS 2R (REMC + matrice GDE) + travail Bruno
 * Llorens, candidat moniteur ECSR — peut être complétée/affinée au fil du temps.
 */

import { FAMILIES } from './classeur7Familles';

export const CAS_DETAILS = [
  // ─── Famille Comportement (1, 4, 26) ──────────────────────────────────────
  {
    num: 1,
    title: "La vitesse excessive sur route ouverte",
    familySlug: 'comportement',
    enonce: "Vous accueillez un groupe de jeunes motards (18-25 ans) ayant obtenu leur permis A2 depuis moins de 6 mois. Plusieurs reconnaissent rouler régulièrement au-dessus des limitations sur les routes départementales. Construisez une action de formation visant à faire évoluer ce comportement.",
    publicAttendu: "Jeunes motards 18-25 ans, permis A2 récent, déjà initiés à la conduite mais sous-évaluant le risque vitesse.",
    leviersAttendus: ['Motivation à progresser', 'Sensibilité aux témoignages', 'Esprit de groupe', 'Volonté de durer'],
    freinsAttendus: ['Sur-confiance liée à l\'expérience récente', 'Pression des pairs', 'Sensation de plaisir liée à la vitesse', 'Sentiment d\'invulnérabilité'],
    objectifs: {
      connaitre: "Connaître les distances de freinage et la cinétique du choc selon la vitesse",
      comprendre: "Comprendre le lien vitesse / gravité / temps de réaction",
      agir: "Agir en adaptant sa vitesse au contexte (visibilité, état chaussée, trafic)"
    },
    methodesAttendues: ['Active', 'Étude de cas', 'Démonstrative'],
    supportsAttendus: ['Vidéo accidentologie', 'Graphique distance de freinage', 'Témoignages écrits'],
    evaluationAttendue: "QCM 5 questions sur les distances de freinage + cas pratique : adapter sa vitesse à 3 situations.",
    pointsCles: ['représentations', 'cinétique du choc', 'prise de conscience', 'transfert vers la conduite'],
  },
  {
    num: 4,
    title: "Le dépassement dangereux en deux-roues",
    familySlug: 'comportement',
    enonce: "Plusieurs élèves de votre auto-école reconnaissent dépasser entre les files lorsque la circulation est dense, parfois à plus de 30 km/h d'écart avec les autres usagers. Construisez votre séance.",
    publicAttendu: "Motards adultes pratiquant la circulation urbaine et péri-urbaine quotidienne.",
    leviersAttendus: ['Volonté de durer', 'Témoignages de pairs', 'Réglementation claire'],
    freinsAttendus: ['Gain de temps perçu', 'Habitude installée', 'Banalisation', 'Sentiment de maîtrise'],
    objectifs: {
      connaitre: "Connaître la réglementation de la circulation inter-files (CIF) et ses limitations",
      comprendre: "Comprendre les angles morts des autres usagers et les zones de chocs frontaux-latéraux",
      agir: "Agir en respectant les conditions strictes de la CIF ou en se rangeant dans la file"
    },
    methodesAttendues: ['Interrogative', 'Étude de cas', 'Démonstrative'],
    supportsAttendus: ['Schéma angles morts', 'Article réglementaire CIF', 'Vidéo crash test'],
    evaluationAttendue: "Cas pratique sur photo : identifier les zones de risque et les conditions de dépassement.",
    pointsCles: ['CIF', 'angle mort', 'comportement à risque', 'transfert'],
  },
  {
    num: 26,
    title: "Les motards qui pratiquent le wheeling et la stunt en ville",
    familySlug: 'comportement',
    enonce: "Plusieurs candidats post-permis A2 reconnaissent pratiquer ou être tentés par des figures (wheeling, stoppie) en ville le week-end. Construisez votre séance.",
    publicAttendu: "Jeunes motards (18-30 ans), sportifs, recherche de sensations et reconnaissance entre pairs.",
    leviersAttendus: ['Pratique encadrée possible (circuits)', 'Témoignages de stunters professionnels', 'Sensibilité juridique'],
    freinsAttendus: ['Pression des pairs', 'Réseaux sociaux valorisants', 'Sentiment d\'expertise', 'Banalisation'],
    objectifs: {
      connaitre: "Connaître les sanctions juridiques (R412-7 du Code de la route, retrait permis)",
      comprendre: "Comprendre la différence entre pratique encadrée et pratique sauvage",
      agir: "Agir en orientant sa pratique vers des espaces dédiés (circuits, stages stunt)"
    },
    methodesAttendues: ['Débat', 'Étude de cas', 'Démonstrative'],
    supportsAttendus: ['Vidéo accident wheeling', 'Sanctions juridiques', 'Témoignage pro'],
    evaluationAttendue: "Débat structuré : Pour / Contre la pratique de figures en ville. Rendu d'une synthèse écrite.",
    pointsCles: ['représentations', 'influence sociale', 'cadre légal', 'prise de conscience'],
  },

  // ─── Famille Risque statistique (2, 10, 11, 14, 15, 17) ───────────────────
  {
    num: 2,
    title: "La mortalité des 2RM en France",
    familySlug: 'risque-statistique',
    enonce: "Les usagers de 2-roues motorisés représentent 1,9 % du trafic mais 21 % des tués sur la route (source ONISR 2023). Construisez une séance pour faire prendre conscience de ce risque réel à un groupe d'apprenants.",
    publicAttendu: "Apprenants permis A1, A2 ou passerelle A, peu sensibilisés au risque réel.",
    leviersAttendus: ['Chiffres factuels ONISR', 'Témoignages de victimes', 'Comparaison avec voiture'],
    freinsAttendus: ['Sentiment que "ça n\'arrive qu\'aux autres"', 'Représentations erronées', 'Banalisation'],
    objectifs: {
      connaitre: "Connaître les chiffres clés de l'accidentalité 2RM (mortalité, gravité, exposition)",
      comprendre: "Comprendre le facteur de sur-représentation du 2RM dans les accidents graves",
      agir: "Agir en intégrant cette donnée dans ses choix de conduite (équipement, allure, anticipation)"
    },
    methodesAttendues: ['Expositive', 'Active', 'Interrogative'],
    supportsAttendus: ['Bilan ONISR', 'Infographies', 'Témoignages vidéo'],
    evaluationAttendue: "QCM 5 questions sur les chiffres + carte heuristique des facteurs de risque.",
    pointsCles: ['ONISR', 'accidentalité', 'exposition au risque', 'gravité'],
  },
  {
    num: 10,
    title: "Le risque sur les trajets de loisir",
    familySlug: 'risque-statistique',
    enonce: "72 % des motards tués sont des conducteurs expérimentés roulant sur une moto lourde lors d'un trajet de loisir (week-end, vacances). Construisez une séance pour des motards de plus de 35 ans.",
    publicAttendu: "Motards expérimentés 35-65 ans, moto type roadster/sport-touring, usage loisir week-end.",
    leviersAttendus: ['Affect (proches)', 'Témoignages pairs', 'Données ONISR', 'Maturité'],
    freinsAttendus: ['Sur-confiance liée à l\'ancienneté', 'Plaisir et passion', 'Sentiment de maîtrise', 'Effet "couloir maîtrisé"'],
    objectifs: {
      connaitre: "Connaître les statistiques tués 2RM par tranche d'âge et type de trajet",
      comprendre: "Comprendre pourquoi l'expérience seule ne protège pas",
      agir: "Agir en s'imposant des règles personnelles (allure, parcours, équipement, briefing en groupe)"
    },
    methodesAttendues: ['Expositive', 'Débat', 'Étude de cas'],
    supportsAttendus: ['Campagne Sécurité routière 2026', 'Données ONISR', 'Témoignages'],
    evaluationAttendue: "Débat structuré + production écrite : 5 règles personnelles à adopter pour mes sorties.",
    pointsCles: ['exposition au risque', 'ONISR', 'sur-confiance', 'gravité'],
  },
  {
    num: 11,
    title: "L'accidentalité des jeunes motards 18-24 ans",
    familySlug: 'risque-statistique',
    enonce: "Les 18-24 ans représentent 10 % de la population française mais 20 % des tués à moto. Construisez une séance pour des candidats au permis A1 (16-17 ans).",
    publicAttendu: "Adolescents 16-17 ans en formation A1, faible expérience routière, fort enthousiasme.",
    leviersAttendus: ['Curiosité', 'Volonté d\'autonomie', 'Sensibilité aux pairs', 'Témoignages de jeunes accidentés'],
    freinsAttendus: ['Sentiment d\'invulnérabilité', 'Recherche de sensations', 'Sous-estimation du risque', 'Influence des pairs'],
    objectifs: {
      connaitre: "Connaître les facteurs spécifiques de risque chez les jeunes (vitesse, alcool, fatigue, pairs)",
      comprendre: "Comprendre le décalage entre perception et exposition réelle",
      agir: "Agir en intégrant des règles de sécurité personnelles dès les premiers kilomètres"
    },
    methodesAttendues: ['Active', 'Interrogative', 'Étude de cas'],
    supportsAttendus: ['Vidéo jeunes accidentés', 'Chiffres ONISR par tranche d\'âge', 'Photos'],
    evaluationAttendue: "Atelier : construire son propre 'kit anti-accident' (5 règles écrites).",
    pointsCles: ['exposition au risque', 'ONISR', 'invulnérabilité', 'prise de conscience'],
  },
  {
    num: 14,
    title: "Les accidents en intersection",
    familySlug: 'risque-statistique',
    enonce: "Près de 50 % des accidents corporels en agglomération impliquant un 2RM ont lieu en intersection (refus de priorité par un autre usager). Construisez votre séance.",
    publicAttendu: "Motards urbains, navetteurs quotidiens, tous niveaux d'expérience.",
    leviersAttendus: ['Données chiffrées', 'Témoignages', 'Vidéos de situations'],
    freinsAttendus: ['Habitude des trajets', 'Sentiment de priorité acquise', 'Effet de routine'],
    objectifs: {
      connaitre: "Connaître les chiffres sur les accidents en intersection et leurs typologies",
      comprendre: "Comprendre la notion de 'visibilité offerte' vs 'visibilité réelle' aux autres usagers",
      agir: "Agir en adoptant la position anti-collision (ralentir, regard, position dans la voie)"
    },
    methodesAttendues: ['Démonstrative', 'Étude de cas', 'Interrogative'],
    supportsAttendus: ['Vidéo refus de priorité', 'Schémas intersections', 'Statistiques ONISR'],
    evaluationAttendue: "Cas pratique sur photo : identifier les zones de risque à 3 intersections différentes.",
    pointsCles: ['accidentalité', 'visibilité', 'ONISR', 'prévention'],
  },
  {
    num: 15,
    title: "L'accidentalité des week-ends de printemps",
    familySlug: 'risque-statistique',
    enonce: "Les premiers week-ends de mars-avril concentrent une part importante des accidents graves de motards (reprise après hiver). Construisez une séance pré-saison.",
    publicAttendu: "Motards expérimentés reprenant la pratique au printemps.",
    leviersAttendus: ['Expérience pratique', 'Conscience du risque', 'Sens du collectif'],
    freinsAttendus: ['Sur-confiance liée à l\'ancienneté', 'Précipitation à reprendre', 'Mauvais entretien hivernal'],
    objectifs: {
      connaitre: "Connaître les pics d'accidentalité saisonniers et leurs causes",
      comprendre: "Comprendre les pertes de réflexes et altérations de la machine après hivernage",
      agir: "Agir en organisant une 'reprise progressive' (contrôle moto, petits trajets, montée en charge)"
    },
    methodesAttendues: ['Expositive', 'Interrogative', 'Démonstrative'],
    supportsAttendus: ['Statistiques mensuelles ONISR', 'Check-list reprise printemps'],
    evaluationAttendue: "Construction collective d'une check-list 'reprise printemps' en 10 points.",
    pointsCles: ['exposition au risque', 'accidentalité', 'gravité', 'reprise'],
  },
  {
    num: 17,
    title: "La gravité des chocs latéraux",
    familySlug: 'risque-statistique',
    enonce: "Le choc latéral représente une part importante de la mortalité 2RM (50 % de la mortalité moto en intersection). Construisez une séance.",
    publicAttendu: "Motards tous niveaux, particulièrement les urbains.",
    leviersAttendus: ['Données chiffrées', 'Témoignages', 'Crash-tests'],
    freinsAttendus: ['Sentiment de pouvoir éviter', 'Sous-estimation de la cinétique', 'Représentations erronées'],
    objectifs: {
      connaitre: "Connaître la cinétique des chocs latéraux et leurs conséquences corporelles",
      comprendre: "Comprendre la quasi-impossibilité d'évitement à courte distance",
      agir: "Agir en plaçant sa moto en position défensive aux intersections (regard, allure)"
    },
    methodesAttendues: ['Démonstrative', 'Expositive', 'Étude de cas'],
    supportsAttendus: ['Vidéos crash test', 'Schémas anatomiques', 'Données ONISR'],
    evaluationAttendue: "QCM cinétique + cas pratique : positionnement défensif sur 3 intersections.",
    pointsCles: ['gravité', 'cinétique', 'ONISR', 'choc latéral'],
  },

  // ─── Famille Réglementation (3, 8, 9) ──────────────────────────────────────
  {
    num: 3,
    title: "L'obligation du port des gants homologués",
    familySlug: 'reglementation',
    enonce: "Depuis novembre 2016, le port des gants homologués (norme EN13594) est obligatoire pour tous les usagers 2RM. Beaucoup ignorent encore cette règle. Construisez votre séance.",
    publicAttendu: "Tous publics motards, du novice au confirmé.",
    leviersAttendus: ['Légalité', 'Données médicales', 'Coût relatif'],
    freinsAttendus: ['Inconfort perçu', 'Habitude', 'Coût psychologique', 'Méconnaissance'],
    objectifs: {
      connaitre: "Connaître la réglementation (article R431-1-2) et la norme EN13594",
      comprendre: "Comprendre l'utilité protectrice des gants (paume, articulations)",
      agir: "Agir en choisissant et portant systématiquement des gants conformes"
    },
    methodesAttendues: ['Expositive', 'Démonstrative', 'Active'],
    supportsAttendus: ['Article législatif', 'Norme EN13594', 'Échantillons gants', 'Photos médicales'],
    evaluationAttendue: "QCM 5 questions réglementation + atelier 'reconnaître un gant conforme'.",
    pointsCles: ['règle', 'compréhension', 'application', 'sécurité'],
  },
  {
    num: 8,
    title: "La vitesse réglementaire sur autoroute pluvieuse",
    familySlug: 'reglementation',
    enonce: "Sur autoroute, la vitesse maximale autorisée passe de 130 à 110 km/h en cas de pluie. Construisez une séance ciblée motards autoroutiers.",
    publicAttendu: "Motards utilisateurs réguliers de l'autoroute (navetteurs, voyageurs).",
    leviersAttendus: ['Cadre légal clair', 'Données physique (adhérence)', 'Témoignages'],
    freinsAttendus: ['Habitude', 'Contestation de la règle', 'Conditions perçues bonnes'],
    objectifs: {
      connaitre: "Connaître les vitesses réglementaires selon météo et type de voie",
      comprendre: "Comprendre l'effet pluie sur l'adhérence et la distance de freinage",
      agir: "Agir en respectant les nouvelles limites + en adaptant son comportement (allure, distance)"
    },
    methodesAttendues: ['Expositive', 'Démonstrative', 'Active'],
    supportsAttendus: ['Code de la route', 'Tables distances freinage', 'Vidéos pluie'],
    evaluationAttendue: "Cas pratique : à 110 km/h sur autoroute mouillée, distance de freinage et de sécurité.",
    pointsCles: ['règle', 'sécurité', 'adhérence', 'application'],
  },
  {
    num: 9,
    title: "Les distances de sécurité",
    familySlug: 'reglementation',
    enonce: "Le Code de la route impose une distance de sécurité équivalente à 2 secondes minimum derrière le véhicule précédent. Construisez votre séance.",
    publicAttendu: "Motards tous publics, particulièrement les conducteurs urbains et autoroutiers.",
    leviersAttendus: ['Règle simple à appliquer', 'Logique physique', 'Sanctions'],
    freinsAttendus: ['Habitude de coller', 'Pression du trafic', 'Sous-estimation des distances de freinage'],
    objectifs: {
      connaitre: "Connaître la règle des 2 secondes et les sanctions",
      comprendre: "Comprendre la relation vitesse / temps de réaction / distance de freinage",
      agir: "Agir en pratiquant le 'comptage' des 2 secondes sur des points fixes"
    },
    methodesAttendues: ['Démonstrative', 'Active', 'Expositive'],
    supportsAttendus: ['Article R412-12', 'Vidéo démonstration', 'Tableau temps/distance'],
    evaluationAttendue: "QCM règles + exercice de comptage 2 secondes en simulation.",
    pointsCles: ['règle', 'application', 'sécurité', 'compréhension'],
  },

  // ─── Famille Véhicule (5, 7, 12, 18, 19, 21) ──────────────────────────────
  {
    num: 5,
    title: "Le contrôle de la pression des pneus",
    familySlug: 'vehicule',
    enonce: "Selon les études, plus de 60 % des motards ne contrôlent pas la pression de leurs pneus avant chaque sortie. Construisez votre séance.",
    publicAttendu: "Tous publics motards, du novice au confirmé.",
    leviersAttendus: ['Geste simple', 'Données techniques', 'Sécurité immédiate'],
    freinsAttendus: ['Oubli', 'Méconnaissance des bonnes pressions', 'Absence de manomètre', 'Précipitation'],
    objectifs: {
      connaitre: "Connaître les bonnes pressions (préconisations constructeur, livre de bord)",
      comprendre: "Comprendre l'influence de la pression sur la tenue de route et la consommation",
      agir: "Agir en vérifiant la pression à froid au moins une fois par mois et avant chaque trajet long"
    },
    methodesAttendues: ['Démonstrative', 'Active', 'Atelier'],
    supportsAttendus: ['Moto en atelier', 'Manomètre', 'Livre de bord', 'Tableaux pressions'],
    evaluationAttendue: "Atelier pratique : chaque élève vérifie et ajuste la pression sur une moto pédagogique.",
    pointsCles: ['contrôle', 'entretien', 'sécurité', 'conformité'],
  },
  {
    num: 7,
    title: "L'usure et le changement des pneus",
    familySlug: 'vehicule',
    enonce: "Un pneu moto usé est l'une des principales causes de chute en virage. Construisez votre séance.",
    publicAttendu: "Motards en formation continue et post-permis.",
    leviersAttendus: ['Données techniques', 'Sécurité immédiate', 'Conformité légale'],
    freinsAttendus: ['Coût psychologique', 'Méconnaissance des témoins d\'usure', 'Procrastination'],
    objectifs: {
      connaitre: "Connaître les témoins d'usure (TWI) et la profondeur minimale (1,6 mm)",
      comprendre: "Comprendre la perte d'adhérence et de tenue de route d'un pneu usé",
      agir: "Agir en contrôlant régulièrement et en remplaçant à temps"
    },
    methodesAttendues: ['Démonstrative', 'Atelier', 'Interrogative'],
    supportsAttendus: ['Pneus usés vs neufs', 'Vidéos chute', 'Norme TWI'],
    evaluationAttendue: "Atelier : 5 pneus différents à classer (neuf, mi-usé, usé, déformé, à remplacer).",
    pointsCles: ['entretien', 'contrôle', 'conformité', 'adhérence'],
  },
  {
    num: 12,
    title: "L'entretien de la chaîne et de la transmission",
    familySlug: 'vehicule',
    enonce: "Une chaîne mal entretenue (sèche, détendue) peut provoquer une rupture et une chute. Construisez votre séance.",
    publicAttendu: "Motards de tous niveaux, particulièrement les nouveaux propriétaires de moto.",
    leviersAttendus: ['Geste simple', 'Coût économique', 'Données techniques'],
    freinsAttendus: ['Méconnaissance', 'Oubli', 'Coût psychologique du temps passé'],
    objectifs: {
      connaitre: "Connaître la fréquence et les gestes d'entretien (lubrification, tension)",
      comprendre: "Comprendre les conséquences d'une chaîne mal entretenue (rupture, blocage roue)",
      agir: "Agir en entretenant régulièrement (tous les 500 km ou après pluie)"
    },
    methodesAttendues: ['Démonstrative', 'Atelier', 'Active'],
    supportsAttendus: ['Moto au sol', 'Lubrifiant', 'Clés', 'Schémas tension'],
    evaluationAttendue: "Atelier pratique : contrôle et lubrification de la chaîne par chaque élève.",
    pointsCles: ['entretien', 'contrôle', 'sécurité', 'conformité'],
  },
  {
    num: 18,
    title: "Le port du casque homologué",
    familySlug: 'vehicule',
    enonce: "Le casque homologué ECE 22-06 sauve des vies. Construisez une séance sur le choix et l'entretien du casque.",
    publicAttendu: "Apprenants permis A1, A2, jeunes motards.",
    leviersAttendus: ['Donnée vitale', 'Cadre légal', 'Évolution norme 22-06'],
    freinsAttendus: ['Coût psychologique', 'Esthétique', 'Confort perçu', 'Méconnaissance des normes'],
    objectifs: {
      connaitre: "Connaître la norme ECE 22-06, les éléments d'un bon casque, sa durée de vie",
      comprendre: "Comprendre l'efficacité protectrice et les choix selon usage",
      agir: "Agir en choisissant, entretenant et remplaçant son casque correctement"
    },
    methodesAttendues: ['Démonstrative', 'Active', 'Expositive'],
    supportsAttendus: ['Casques (différents types et états)', 'Norme ECE', 'Vidéos tests'],
    evaluationAttendue: "Atelier : reconnaître les casques conformes parmi 5 modèles différents.",
    pointsCles: ['équipement', 'conformité', 'sécurité', 'entretien'],
  },
  {
    num: 19,
    title: "L'équipement complet du motard (EPI)",
    familySlug: 'vehicule',
    enonce: "L'équipement complet du motard (casque, gants, blouson, pantalon, chaussures) divise par 3 les blessures graves en cas de chute. Construisez votre séance.",
    publicAttendu: "Tous publics motards, particulièrement les urbains et les jeunes.",
    leviersAttendus: ['Données médicales', 'Évolution technologique des EPI', 'Confort actuel'],
    freinsAttendus: ['Coût économique', 'Look', 'Confort en ville', 'Habitude'],
    objectifs: {
      connaitre: "Connaître les normes (CE, FFP1, EN13595, EN13594) pour chaque pièce d'EPI",
      comprendre: "Comprendre la protection apportée par chaque élément et les zones critiques",
      agir: "Agir en s'équipant intégralement et en remplaçant après chute"
    },
    methodesAttendues: ['Démonstrative', 'Étude de cas', 'Atelier'],
    supportsAttendus: ['EPI complets', 'Schémas zones de chute', 'Vidéos crash test'],
    evaluationAttendue: "Cas pratique : composer l'EPI complet à partir d'un catalogue avec un budget donné.",
    pointsCles: ['équipement', 'conformité', 'sécurité', 'EPI'],
  },
  {
    num: 21,
    title: "Le contrôle technique pour les motos (à partir de 2025)",
    familySlug: 'vehicule',
    enonce: "Le contrôle technique des motos est devenu obligatoire en France. Construisez une séance d'information à destination des motards.",
    publicAttendu: "Tous motards propriétaires d'une moto.",
    leviersAttendus: ['Information factuelle', 'Cadre réglementaire', 'Sécurité collective'],
    freinsAttendus: ['Contestation politique', 'Coût économique', 'Méconnaissance des modalités'],
    objectifs: {
      connaitre: "Connaître le calendrier, les points contrôlés, le coût et la périodicité",
      comprendre: "Comprendre les enjeux de sécurité et d'environnement",
      agir: "Agir en préparant sa moto au CT et en respectant les obligations"
    },
    methodesAttendues: ['Expositive', 'Interrogative', 'Atelier'],
    supportsAttendus: ['Décret officiel', 'Check-list points contrôlés', 'FAQ'],
    evaluationAttendue: "Atelier : préparer une moto pédagogique pour le passage au CT (check-list 10 points).",
    pointsCles: ['contrôle', 'conformité', 'entretien', 'application'],
  },

  // ─── Famille Physiologique (6, 22, 23) ────────────────────────────────────
  {
    num: 6,
    title: "La fatigue au guidon",
    familySlug: 'physiologique',
    enonce: "La fatigue est responsable de près d'un accident mortel sur trois sur autoroute. Construisez une séance pour des motards effectuant régulièrement de longs trajets.",
    publicAttendu: "Motards utilisateurs réguliers d'autoroute, voyageurs, navetteurs longue distance.",
    leviersAttendus: ['Volonté de se protéger', 'Données médicales', 'Témoignages'],
    freinsAttendus: ['Pression de l\'objectif', 'Sous-estimation de la fatigue', 'Volonté de "finir"'],
    objectifs: {
      connaitre: "Connaître les signes de fatigue (bâillements, paupières lourdes, perte attention)",
      comprendre: "Comprendre la baisse de vigilance progressive et inconsciente",
      agir: "Agir en s'arrêtant toutes les 2 heures et en respectant son rythme physiologique"
    },
    methodesAttendues: ['Expositive', 'Interrogative', 'Étude de cas'],
    supportsAttendus: ['Schémas chronobiologie', 'Témoignages vidéo', 'Données médicales'],
    evaluationAttendue: "Production écrite : ma stratégie 'long trajet' en 5 règles personnelles.",
    pointsCles: ['vigilance', 'fatigue', 'perception', 'physiologique'],
  },
  {
    num: 22,
    title: "L'alcool et la conduite moto",
    familySlug: 'physiologique',
    enonce: "L'alcool est présent dans environ 30 % des accidents mortels de motards. Construisez votre séance.",
    publicAttendu: "Motards 20-40 ans, particulièrement ceux fréquentant des concentrations ou bars motards.",
    leviersAttendus: ['Données médicales', 'Témoignages familles victimes', 'Cadre légal'],
    freinsAttendus: ['Convivialité', 'Pression sociale', 'Sous-estimation des effets', 'Plaisir social'],
    objectifs: {
      connaitre: "Connaître les seuils légaux (0,5 et 0,2 g/l pour permis probatoire) et les sanctions",
      comprendre: "Comprendre les effets neurologiques de l'alcool sur la conduite (réflexes, jugement, vision)",
      agir: "Agir en s'organisant en amont (capitaine de soirée, taxi, hébergement)"
    },
    methodesAttendues: ['Active', 'Étude de cas', 'Démonstrative'],
    supportsAttendus: ['Lunettes simulation alcool', 'Témoignages', 'Statistiques'],
    evaluationAttendue: "Mises en situation : 'Vous êtes en soirée, comment vous organisez votre retour ?'",
    pointsCles: ['alcool', 'perception', 'vigilance', 'physiologique'],
  },
  {
    num: 23,
    title: "Les stupéfiants et la conduite",
    familySlug: 'physiologique',
    enonce: "Le cannabis est détecté dans environ 23 % des accidents mortels de jeunes conducteurs. Construisez une séance pour des candidats au permis A1/A2.",
    publicAttendu: "Apprenants 16-25 ans en formation initiale.",
    leviersAttendus: ['Données médicales', 'Sanctions sévères', 'Cadre légal'],
    freinsAttendus: ['Sous-estimation des effets', 'Banalisation', 'Pression des pairs', 'Refus du sujet'],
    objectifs: {
      connaitre: "Connaître les effets neurologiques des stupéfiants sur les capacités de conduite",
      comprendre: "Comprendre la durée d'élimination (cannabis détectable plusieurs jours)",
      agir: "Agir en s'abstenant totalement avant de prendre le guidon"
    },
    methodesAttendues: ['Expositive', 'Interrogative', 'Étude de cas'],
    supportsAttendus: ['Données médicales', 'Témoignages', 'Tableau sanctions'],
    evaluationAttendue: "QCM effets et sanctions + cas pratique : 'Que faire si je suis sous influence ?'",
    pointsCles: ['perception', 'vigilance', 'physiologique', 'sanctions'],
  },

  // ─── Famille Physique (24, 25, 27) ────────────────────────────────────────
  {
    num: 24,
    title: "L'adhérence en virage",
    familySlug: 'physique',
    enonce: "Beaucoup de chutes en virage sont liées à une mauvaise gestion de l'adhérence (vitesse, état chaussée, angle). Construisez votre séance.",
    publicAttendu: "Motards post-permis A2 et confirmés.",
    leviersAttendus: ['Sensations propres', 'Données techniques', 'Démonstrations'],
    freinsAttendus: ['Sur-estimation des capacités', 'Confiance excessive', 'Sous-estimation des facteurs externes'],
    objectifs: {
      connaitre: "Connaître les facteurs influençant l'adhérence (revêtement, pluie, gravillons, gomme pneu)",
      comprendre: "Comprendre la notion de 'budget d'adhérence' (Kamm)",
      agir: "Agir en adaptant la vitesse d'approche, l'angle et la trajectoire"
    },
    methodesAttendues: ['Démonstrative', 'Étude de cas', 'Active'],
    supportsAttendus: ['Cercle de Kamm', 'Vidéos chutes virages', 'Schémas trajectoires'],
    evaluationAttendue: "Analyse de 3 chutes vidéo : identifier les causes et les solutions.",
    pointsCles: ['adhérence', 'trajectoire', 'physique', 'sécurité'],
  },
  {
    num: 25,
    title: "Le freinage d'urgence en moto",
    familySlug: 'physique',
    enonce: "Le freinage d'urgence en moto demande une technique précise sous peine de chute. Construisez une séance.",
    publicAttendu: "Motards post-permis A1/A2 et formation continue.",
    leviersAttendus: ['Geste technique apprenable', 'Sensations propres', 'Données physiques'],
    freinsAttendus: ['Peur du freinage avant', 'Réflexe de freiner uniquement à l\'arrière', 'Sur-confiance'],
    objectifs: {
      connaitre: "Connaître la répartition du freinage (70 % avant / 30 % arrière)",
      comprendre: "Comprendre le transfert de masse vers l'avant et son influence sur l'adhérence",
      agir: "Agir en pratiquant le freinage progressif des deux freins sur exercice plateau"
    },
    methodesAttendues: ['Démonstrative', 'Atelier', 'Active'],
    supportsAttendus: ['Plateau', 'Vidéos freinage', 'Schémas transfert masse'],
    evaluationAttendue: "Exercice plateau : freinage d'urgence à 50 km/h évalué sur la distance et la stabilité.",
    pointsCles: ['freinage', 'transfert de masse', 'adhérence', 'physique'],
  },
  {
    num: 27,
    title: "La trajectoire de sécurité",
    familySlug: 'physique',
    enonce: "La trajectoire de sécurité (extérieur-intérieur-extérieur) est un fondamental moto. Construisez votre séance.",
    publicAttendu: "Apprenants post-permis A2 et passerelle A.",
    leviersAttendus: ['Sensations propres', 'Sécurité visible', 'Plaisir de la conduite fluide'],
    freinsAttendus: ['Habitude de "couper" les virages', 'Sur-estimation des capacités', 'Méconnaissance'],
    objectifs: {
      connaitre: "Connaître les 3 points clés (entrée, corde, sortie) et la notion de visibilité",
      comprendre: "Comprendre comment cette trajectoire élargit le champ visuel et la marge de sécurité",
      agir: "Agir en pratiquant cette trajectoire sur un parcours imposé"
    },
    methodesAttendues: ['Démonstrative', 'Atelier', 'Active'],
    supportsAttendus: ['Schémas trajectoires', 'Vidéos', 'Parcours imposé'],
    evaluationAttendue: "Exercice plateau : 5 virages successifs en trajectoire de sécurité, évalué par le formateur.",
    pointsCles: ['trajectoire', 'sécurité', 'adhérence', 'physique'],
  },

  // ─── Famille Conscientisation (13, 16, 20) ────────────────────────────────
  {
    num: 13,
    title: "L'influence des pairs et des réseaux sociaux",
    familySlug: 'conscientisation',
    enonce: "Les jeunes motards sont fortement influencés par les contenus motos sur YouTube/TikTok (rides, wheelings). Construisez une séance.",
    publicAttendu: "Apprenants 16-25 ans, forte exposition aux réseaux sociaux moto.",
    leviersAttendus: ['Esprit critique', 'Données factuelles', 'Témoignages alternatifs (motards prudents)'],
    freinsAttendus: ['Identification aux stars du ride', 'Effet de groupe', 'Recherche de reconnaissance'],
    objectifs: {
      connaitre: "Connaître les biais de représentation des contenus moto sur les réseaux",
      comprendre: "Comprendre la différence entre pratique réelle et pratique mise en scène",
      agir: "Agir en développant son esprit critique et en choisissant ses modèles"
    },
    methodesAttendues: ['Débat', 'Étude de cas', 'Interrogative'],
    supportsAttendus: ['Extraits réseaux sociaux', 'Statistiques ONISR', 'Témoignages'],
    evaluationAttendue: "Atelier : analyser 3 vidéos populaires et identifier ce qui n'est pas montré (chutes, accidents).",
    pointsCles: ['influence sociale', 'représentations', 'esprit critique', 'prise de conscience'],
  },
  {
    num: 16,
    title: "La sur-estimation de ses capacités",
    familySlug: 'conscientisation',
    enonce: "Plus de 80 % des motards se considèrent comme 'meilleurs que la moyenne'. Construisez une séance pour faire prendre conscience de ce biais.",
    publicAttendu: "Motards expérimentés post-permis, particulièrement les confirmés.",
    leviersAttendus: ['Démonstrations pratiques', 'Données psychologiques (effet Dunning-Kruger)', 'Mises en situation'],
    freinsAttendus: ['Ego', 'Refus de remise en cause', 'Résistance au changement'],
    objectifs: {
      connaitre: "Connaître le biais de sur-estimation (effet Dunning-Kruger appliqué à la conduite)",
      comprendre: "Comprendre comment ce biais nous met en danger",
      agir: "Agir en se formant régulièrement (stages, perfectionnement) et en pratiquant l'auto-évaluation honnête"
    },
    methodesAttendues: ['Interrogative', 'Démonstrative', 'Étude de cas'],
    supportsAttendus: ['Études psychologiques', 'Tests d\'auto-évaluation', 'Témoignages'],
    evaluationAttendue: "Auto-évaluation guidée + comparaison avec évaluation du formateur sur exercice plateau.",
    pointsCles: ['surestimation', 'représentations', 'prise de conscience', 'auto-évaluation'],
  },
  {
    num: 20,
    title: "Le rapport au risque chez le motard",
    familySlug: 'conscientisation',
    enonce: "Le motard a un rapport au risque particulier (recherche de sensations, plaisir, dépassement). Construisez une séance pour aider à canaliser ce rapport.",
    publicAttendu: "Motards de tous âges, particulièrement les sportifs et les passionnés.",
    leviersAttendus: ['Pratique encadrée (circuits, stages)', 'Témoignages', 'Identification de modèles positifs'],
    freinsAttendus: ['Plaisir de la prise de risque', 'Banalisation', 'Identification à des modèles dangereux'],
    objectifs: {
      connaitre: "Connaître les concepts de risque pris vs risque subi, et les profils accidentogènes",
      comprendre: "Comprendre comment canaliser le besoin de sensations vers des cadres adaptés",
      agir: "Agir en pratiquant en circuit, en stage perfectionnement, en groupe encadré"
    },
    methodesAttendues: ['Débat', 'Étude de cas', 'Interrogative'],
    supportsAttendus: ['Témoignages motards encadrés', 'Calendrier événements pistards', 'Études'],
    evaluationAttendue: "Production écrite : 'Comment je canalise mon goût du risque cette année ?' (plan en 5 points).",
    pointsCles: ['perception', 'influence sociale', 'prise de conscience', 'transfert'],
  },
];

/**
 * Contexte de séance standardisé par famille CCS — durée + effectif typique.
 * L'aspect pédagogique est dérivé de `FAMILIES[slug].aspect` (source de vérité
 * dans `classeur7Familles.js`) afin d'éviter toute désynchronisation.
 *
 * Valeurs alignées sur la pratique CCS 2 Roues : séance type 1h30 pour la
 * majorité ; 2h pour les sujets « Comportement » et « Conscientisation »
 * qui demandent du débat ; 1h pour les sujets « Réglementation /
 * Véhicule / Physique » plus magistraux.
 */
export const CONTEXT_BY_FAMILY = {
  comportement:       { duree: '2 h',     participants: '6 à 8 apprenants' },
  'risque-statistique': { duree: '1 h 30', participants: '8 à 12 apprenants' },
  reglementation:     { duree: '1 h',     participants: '8 à 12 apprenants' },
  vehicule:           { duree: '1 h',     participants: '6 à 10 apprenants' },
  physiologique:      { duree: '1 h 30', participants: '6 à 8 apprenants' },
  physique:           { duree: '1 h',     participants: '8 à 12 apprenants' },
  conscientisation:   { duree: '2 h',     participants: '6 à 10 apprenants' },
};

const DEFAULT_CONTEXT = {
  duree: '1 h 30',
  participants: '6 à 8 apprenants',
};

/**
 * Retourne le cas par son numéro, enrichi avec son contexte de séance :
 *   - duree         : durée pédagogique standardisée par famille
 *   - participants  : effectif typique
 *   - aspect        : aspect pédagogique officiel (depuis FAMILIES)
 *   - familyTitle   : libellé long de la famille
 *
 * Utilisé directement par le simulateur CCS dès le tirage au sort.
 */
export function getCasByNum(num) {
  const cas = CAS_DETAILS.find((c) => c.num === num);
  if (!cas) return null;
  const family = FAMILIES.find((f) => f.slug === cas.familySlug);
  const ctx = CONTEXT_BY_FAMILY[cas.familySlug] || DEFAULT_CONTEXT;
  return {
    ...cas,
    duree: cas.duree || ctx.duree,
    participants: cas.participants || ctx.participants,
    aspect: cas.aspect || family?.aspect || 'Aspect à préciser',
    familyTitle: family?.title || cas.familySlug,
  };
}
